import { useQuery, UseQueryResult } from 'react-query';
import { getDocumentScan, getDocumentScanLlmTrace, getDocumentScans } from 'src/api/documentScans';
import { DocumentScanListItemResponse, DocumentScanResponse } from 'common/lib/model/documentScan/DocumentScan';
import { DocumentScanLlmTraceResponse } from 'common/lib/model/documentScan/DocumentScanTrace';
import { Encounter, FilterQueryParams } from 'src/utils/types';

export const documentScansKey = ['documentScans'] as const;

export type DocumentScanListItem = DocumentScanListItemResponse;

export interface DocumentScan extends Omit<DocumentScanResponse, 'assets' | 'encounter' | 'extractedData' | 'scanMetadata'> {
    scanMetadata: Record<string, any>;
    extractedData: Record<string, any> | null;
    assets?: Array<{
        documentScanAssetId: string;
        documentScanId: string;
        assetType: string;
        assetUri: string;
        status: string;
        errorMessage: string | null;
        assetMetadata?: Record<string, any>;
        createdAt?: Date;
        lastUpdatedAt?: Date;
        auditVersion: number;
        viewUrl?: string | null;
        content?: string | any | null;
    }>;
    encounter?: Encounter | null;
    displayName?: string;
    scanTypeKey?: string;
    status?: string | null;
    documentType?: Record<string, any> | null;
    entityData?: Record<string, any> | null;
    entityUpdate?: Record<string, any> | null;
    createdEntity?: boolean;
    updatedEntity?: boolean;
    documentScanTypeDetail?: Record<string, any> | null;
    llmTraceAvailable?: boolean;
    llmTraceCount?: number;
}

export interface DocumentScansResponse {
    result: DocumentScanListItem[];
    count: number;
}

export interface UseDocumentScansResult {
    data: DocumentScanListItem[];
    count: number;
}

export const useDocumentScans = (
    facilityId: number,
    orgId: number | undefined,
    params?: FilterQueryParams & { hasEncounter?: boolean; encounterNumber?: string },
): UseQueryResult<UseDocumentScansResult> => {
    return useQuery({
        queryKey: [
            ...documentScansKey,
            orgId,
            facilityId,
            params?.hasEncounter,
            params?.encounterNumber,
            params?.pagination,
        ],
        queryFn: async () => {
            const response = await getDocumentScans(facilityId, params);
            const data = (response.result || []).map((scan) => {
                return {
                    ...scan,
                    createdAt: scan.createdAt ? new Date(scan.createdAt as any) : undefined,
                };
            });
            return {
                data,
                count: response.count,
            };
        },
        enabled: !!facilityId, // Only run query if facilityId is provided
    });
};

export const useDocumentScan = (documentScanId: string | undefined): UseQueryResult<DocumentScan> => {
    return useQuery({
        queryKey: [...documentScansKey, documentScanId],
        queryFn: async () => {
            const response = await getDocumentScan(documentScanId!);
            // Parse date strings to Date objects
            return {
                ...response,
                createdAt: response.createdAt ? new Date(response.createdAt as any) : undefined,
                lastUpdatedAt: response.lastUpdatedAt ? new Date(response.lastUpdatedAt as any) : undefined,
                archivedAt: response.archivedAt ? new Date(response.archivedAt as any) : null,
                assets: response.assets?.map((asset: DocumentScan['assets'][0]) => ({
                    ...asset,
                    createdAt: asset.createdAt ? new Date(asset.createdAt as any) : undefined,
                    lastUpdatedAt: asset.lastUpdatedAt ? new Date(asset.lastUpdatedAt as any) : undefined,
                })),
            };
        },
        enabled: !!documentScanId,
    });
};

export const useDocumentScanLlmTrace = (
    documentScanId: string | undefined,
    enabled: boolean,
): UseQueryResult<DocumentScanLlmTraceResponse> => {
    return useQuery({
        queryKey: [...documentScansKey, documentScanId, 'llmTrace'],
        queryFn: async () => getDocumentScanLlmTrace(documentScanId!),
        enabled: !!documentScanId && enabled,
    });
};
